import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { Alert, ItemViewerNames } from "@app2/clients/types/alert-typedefs";
import { localization } from "@app2/shared/localization/localization";

@Component({
    selector: "alert-selector",
    template: `
        <div *dsLoading="loading"
             class="alert-selector-container">
            <div *ngIf="alerts.length === 0"
                 class="vh-center-content"
                 test-class="no-more-alerts">
                {{ emptyMessageKey | localize }}
            </div>
            <ul #alertList *ngIf="alerts.length"
                class="no-list-styles scrolling-panel" id="alert-scrolling">
                <li class="alert-list-item click-target"
                    *ngFor="let alert of alerts"
                    (click)="onAlertSelected.emit(alert)"
                    [class.active]="alert.id === selectedAlertId || alert.triggerId === selectedTriggerId"
                    test-class="alert"
                    [attr.test-id]="alert.id">
                    <div class="clearfix">
                        <section>
                            <severity-icon [severity]="alert.severity"></severity-icon>
                            <strong>{{ alert.triggerName }}</strong>
                            <span *ngIf="alert.type === 'threat'"
                                  class="de-emphasize">
                                [{{ "THREAT_MATCH" | localize }}]
                            </span>
                        </section>
                        <div>
                            <span *ngIf="alert.currentState === 'NEW'"
                                  class="de-emphasize">
                                {{ alert.resultTimestamp | intervalShortDate }}
                            </span>
                            <span *ngIf="alert.currentState !== 'NEW'"
                                  class="de-emphasize">
                                {{ alert.lastModified | intervalShortDate }}
                            </span>
                            <span *ngIf="alert.totalAlerts | isNumber"
                                  class="right label secondary radius round alert-badge">
                                {{ alert.totalAlerts }}
                                <i class="far fa-bell"></i>
                            </span>
                            <span class="right" *ngIf="viewers[alert.id]" test-class="viewers">
                                <i class="far fa-eye" [matTooltip]="getViewersMessage(alert)"></i>
                                {{viewers[alert.id].length}}
                            </span>
                        </div>
                    </div>
                </li>
                <li *ngIf="canLoadMore"
                    class="alert-list-item loading-container"
                    test-id="load-more">
                    <ng-container *ngIf="loadingMore">
                        <i class="fa fa-spinner fa-spin"></i> {{ 'LOADING' | localize }}
                    </ng-container>
                    <a *ngIf="!loadingMore" (click)="onRequestMore.emit()"
                       class="link-text">{{ 'LOAD_MORE' | localize }}</a>
                </li>
            </ul>
            <div *ngIf=canScrollList()
                 class="flex-container flex-right return-to-top-container">
                <span class="link-text click-target"
                      (click)="scrollToTopOfList()">
                    {{ "RETURN_TO_TOP" | localize }} <i class="far fa-arrow-up return-to-top-icon"></i>
                </span>
            </div>
        </div>
    `,
    host: {
        style: "display: contents;",
    },
    styles: [`
        @import "/src/styles/colors";
        @import "/src/styles/settings";

        .return-to-top-container {
            padding: 12px;
        }

        .return-to-top-icon {
            vertical-align: middle;
        }

        .alert-selector-container {
            display: flex;
            flex-direction: column;
            overflow-y: auto;
        }

        .alert-list-item {
            font-size: 0.875rem;
            border-bottom: $border-rule;
            padding: 8px 0 8px 4px;
            border-left: 4px solid transparent;

            &:hover, &.active {
                background-color: $body-bg-color;

                &:not(.loading-container) {
                    border-left: 4px solid $main-accent-color;
                }
            }

            .alert-badge {
                margin: 4px 6px 0 0;
                background-color: $content-bg-color;
                color: $gray-6;
                border: solid 1px $gray-6;
                padding-top: 6px;
            }
        }
    `],
})
export class AlertSelectorComponent {
    @Input() alerts: Alert[];
    @Input() selectedAlertId: uuid;
    @Input() selectedTriggerId: uuid;
    @Input() loading: boolean;
    @Input() emptyMessageKey: string;
    @Input() viewers: ItemViewerNames = {};
    @Input() loadingMore: boolean;
    @Input() canLoadMore: boolean;

    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onAlertSelected = new EventEmitter<Alert>();
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    @Output() onRequestMore = new EventEmitter<void>();

    @ViewChild("alertList") alertList: ElementRef;

    getViewersMessage(alert): string {
        return localization.getString("VIEWED_IN_PAST_10_SECONDS_BY") + ": " + this.viewers[alert.id].join(", ");
    }

    canScrollList(): boolean {
        return this.alertList?.nativeElement.scrollHeight > this.alertList?.nativeElement.clientHeight;
    }

    scrollToTopOfList(): void {
        this.alertList.nativeElement.scrollTop = 0;
    }
}
